import { FC, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ResizeCell, ResizeCellProps } from '../ResizeCell/ResizeCell';
import { PopoverAddMotive } from '../PopoverAddMotive/PopoverAddMotive';
import {
  PopoverAddMotiveLabelsProps,
  PopoverAddMotiveProps,
} from '../PopoverAddMotive/PopoverAddMotive.types';

interface ContainerCloneCellProps {
  resizeCellProps: ResizeCellProps;
  popoverAddMotiveProps: PopoverAddMotiveProps;
  labelsPopoverAddStop: PopoverAddMotiveLabelsProps;
  timeRange: [number, number];
  widthPerMinute: number;
  totalWidth: number;
  totalHeight: number;
  timezone: string;
  hours: number[];
  event: React.MouseEvent<HTMLElement>;
  removeNode: () => void;
}

export type ResizeHandle = 's' | 'w' | 'e' | 'n' | 'sw' | 'nw' | 'se' | 'ne';

export const ContainerCloneCell: FC<ContainerCloneCellProps> = ({
  resizeCellProps,
  popoverAddMotiveProps,
  labelsPopoverAddStop,
  timeRange,
  widthPerMinute,
  hours,
  timezone,
  event,
  removeNode,
}) => {
  const [timeRangeSelected, setTimeRangeSelected] = useState<[number, number]>([
    timeRange[0],
    timeRange[1],
  ]);
  const containerCellRef = useRef<HTMLDivElement>(null);
  const divHourRef = useRef<HTMLDivElement>(null);
  const hourInit = dayjs.unix(timeRange[0]).tz(timezone).hour();
  const [isDragging, setIsDragging] = useState(false);
  const [handleSelected, setHandleSelected] = useState<ResizeHandle>('w');
  const [popoverOpen, setPopoverOpen] = useState(true);
  const [isResized, setIsResized] = useState(false);
  let pointerCaptureElement: HTMLDivElement | null = null;
  const heightCell = resizeCellProps.height - 4;

  useEffect(() => {
    if (popoverAddMotiveProps.popRef.current) {
      popoverAddMotiveProps.popRef.current.event = event;
    }
  }, [popoverAddMotiveProps.popRef.current]);

  const getNewInitTime = (diffWidth: number, minInit: number, diffMinMinute: number) => {
    const minutesSelected = Math.floor(diffWidth / widthPerMinute);
    const minutes = minutesSelected < minInit ? 0 : minutesSelected - minInit;
    let newInitTime = dayjs.unix(timeRange[0]).add(minutes, 'minute');
    const endTimeSelected = dayjs.unix(timeRangeSelected[1]);
    if (endTimeSelected.diff(newInitTime, 'minute') < diffMinMinute) {
      newInitTime = endTimeSelected.subtract(diffMinMinute, 'minute');
    }
    return newInitTime;
  };

  const getNewEndTime = (diffWidth: number, minInit: number, diffMinMinute: number) => {
    const maxInit = dayjs.unix(timeRange[1]).diff(dayjs.unix(timeRange[0]), 'minute') + minInit;
    const minutesSelected = Math.round(diffWidth / widthPerMinute);
    const minutes = minutesSelected > maxInit ? maxInit - minInit : minutesSelected - minInit;
    let newEndTime = dayjs.unix(timeRange[0]).add(minutes, 'minute').second(0);
    const startTimeSelected = dayjs.unix(timeRangeSelected[0]);
    if (newEndTime.diff(startTimeSelected, 'minute') < diffMinMinute) {
      newEndTime = startTimeSelected.add(diffMinMinute, 'minute');
    }
    return newEndTime;
  };

  const handleChangeSchedule = (handle: ResizeHandle, diffWidth: number) => {
    const minInit = dayjs.unix(timeRange[0]).tz(timezone).minute();
    const diffMinMinute = widthPerMinute >= 20 ? 2 : widthPerMinute >= 17 ? 3 : 4;
    if (handle === 'e') {
      const newEndTime = getNewEndTime(diffWidth, minInit, diffMinMinute);
      setTimeRangeSelected([timeRangeSelected[0], newEndTime.unix()]);
    } else {
      const newInitTime = getNewInitTime(diffWidth, minInit, diffMinMinute);
      setTimeRangeSelected([newInitTime.unix(), timeRangeSelected[1]]);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isDragging) {
      const parent = containerCellRef.current;
      const parentRect = parent!.getBoundingClientRect();
      let offsetWidth = e.clientX - parentRect.x;

      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const divHour = elements.find((element) => element.id.includes('hour-'));
      let currentHour = -1;
      if (divHour) {
        currentHour = Number(divHour?.id.split('-')[1]);
      } else {
        const mainContainer = elements[0];
        if (mainContainer === containerCellRef.current) {
          const children = Array.from(mainContainer.children);
          const targetChild = children.find((child) => {
            const rect = child.getBoundingClientRect();
            return e.clientY <= rect.bottom + 3;
          });

          if (targetChild) {
            currentHour = Number(targetChild?.id.split('-')[1]);
          }
        }
      }

      const indexInit = hours.findIndex((value) => value === hourInit);
      const indexCurrent = hours.findIndex((value) => value === currentHour);

      if (currentHour !== hourInit) {
        offsetWidth = parentRect.width * (indexCurrent - indexInit) + offsetWidth;
      }

      handleChangeSchedule(handleSelected, offsetWidth);
    }
  };

  const handlePointerUp = (e: PointerEvent) => {
    setIsDragging(false);
    const { popRef } = popoverAddMotiveProps;
    if (popRef.current) {
      popRef.current.event = e;
    }
    if (pointerCaptureElement) {
      pointerCaptureElement.releasePointerCapture(e.pointerId);
      pointerCaptureElement = null;
    }
    setPopoverOpen(true);
    setIsResized(true);

    document.removeEventListener('pointerup', handlePointerUp);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const element = document.elementFromPoint(e.clientX, e.clientY);
    if (element?.className.match(/control__pointer/)) {
      e.preventDefault();

      const handle = element?.className.match(/pointer-w/) ? 'w' : 'e';
      setHandleSelected(handle as ResizeHandle);
      setIsDragging(true);

      e.currentTarget.setPointerCapture(e.pointerId);
      pointerCaptureElement = e.currentTarget;

      document.addEventListener('pointerup', handlePointerUp);
    }
  };

  const handleClickOutsideCell = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (el && !el.className.match(/allie-resizable-box/)) {
      removeNode();
    }
    setPopoverOpen(false);
  };

  const startAt = dayjs.unix(timeRangeSelected[0]).second(0).tz(timezone);
  const endAt = dayjs.unix(timeRangeSelected[1]).second(0).tz(timezone);
  const endHour = endAt.hour();
  const startHour = startAt.hour();

  return (
    <div
      ref={containerCellRef}
      style={{
        width: '100%',
        height: '100%',
        touchAction: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
      {...(isDragging
        ? {
            onPointerMove: handlePointerMove,
            onPointerCancel: () => {
              setIsDragging(false);
            },
          }
        : null)}
      {...(!isDragging && { onMouseDown: handleClickOutsideCell })}
      onPointerDown={handlePointerDown}
    >
      {hours.map((hour, index) => {
        const startMinute = hour === startHour ? startAt.minute() : 0;
        const endMinute = hour === endHour ? endAt.minute() : 60;
        const width = widthPerMinute * (endMinute - startMinute);
        const divId = `hour-${hour}`;

        const isTheHourInRange = () => {
          if (startHour <= endHour) {
            return hour >= startHour && hour <= endHour && endMinute > 0;
          } else {
            return (hour >= startHour || hour <= endHour) && endMinute > 0;
          }
        };

        const hideControl = !!popoverAddMotiveProps.onClickDeleteStop;

        return (
          <div
            key={index}
            id={divId}
            style={{
              position: 'relative',
              top: '1px',
              height: `${heightCell}px`,
              marginBottom: '3px',
            }}
            ref={divHourRef}
          >
            {isTheHourInRange() && (
              <ResizeCell
                {...resizeCellProps}
                width={width}
                height={heightCell}
                isStart={hour === startHour && !hideControl}
                isEnd={
                  (hour === endHour && endMinute > 0) ||
                  (hour === endHour - 1 && endAt.minute() === 0 && !hideControl)
                }
                offsetLeft={startMinute * widthPerMinute}
              />
            )}
          </div>
        );
      })}
      <PopoverAddMotive
        {...popoverAddMotiveProps}
        {...labelsPopoverAddStop}
        timeRange={timeRangeSelected}
        isOpen={popoverOpen}
        setIsOpen={setPopoverOpen}
        isResized={isResized}
        originalTimeRange={timeRange}
      />
    </div>
  );
};
